import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  input,
  Input,
  InputSignal,
  model,
  Output,
  signal,
  viewChild,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import { UiIconModel } from '../../types/ui.icon.model';
import { UBaseInfraComponent } from '../u-base-infra.component';
import { Tooltip } from 'primeng/tooltip';
import { isEnterKey } from '@local/ts-infra';
import { AbstractControl, FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

type IconPos = 'right' | 'left';
type InputTooltipPos = 'right' | 'left' | 'top' | 'bottom';
type ClearIconBehavior = 'never' | 'filled-only' | 'search' | 'focus-and-dirty' | 'focus';
type StyleType = 'regular' | 'inline' | 'group';

@UntilDestroy()
@Component({
  selector: 'u-input',
  templateUrl: './u-input.component.html',
  styleUrls: ['./u-input.component.scss'],
})
export class UInputComponent extends UBaseInfraComponent {
  private onCopy: WritableSignal<boolean> = signal(false);
  private isOverflowText: WritableSignal<boolean> = signal(false);

  readonly placeholder: InputSignal<string> = input(null);
  readonly inputType: InputSignal<string> = input('text');
  readonly maxLength: InputSignal<number> = input();
  readonly errorText: InputSignal<string> = input('');
  readonly icon: InputSignal<UiIconModel> = input();
  readonly iconPos: InputSignal<IconPos> = input('left');
  readonly groupContent: InputSignal<UiIconModel | string> = input('');
  readonly hasCopyIcon: InputSignal<boolean> = input(false);
  readonly styles: InputSignal<any> = input({});
  readonly label: InputSignal<string> = input('');
  readonly tooltipRightIcon: InputSignal<string> = input('Clear');
  readonly clearIconBehavior: InputSignal<ClearIconBehavior> = input('filled-only');
  readonly styleType: InputSignal<StyleType> = input('regular');
  readonly autoFitWidthEnabled: InputSignal<boolean> = input(false);
  readonly autoFitWidthMin: InputSignal<number> = input(20);
  readonly autoFitWidthMax: InputSignal<number> = input(240);
  readonly selectTextAfterInit: InputSignal<boolean> = input(false);
  readonly editingHighlight: InputSignal<boolean> = input(false);
  readonly editingLightweightText: InputSignal<boolean> = input(false);
  readonly positionOverflowTooltip: InputSignal<string> = input('top');
  readonly fieldControl: InputSignal<AbstractControl> = input<AbstractControl>();
  readonly formControl = computed<FormControl | null>(() => {
    const control = this.fieldControl();
    return control instanceof FormControl ? (control as FormControl) : null;
  });
  readonly errorsTexts: InputSignal<{ [key: string]: string }> = input<{ [key: string]: string }>({});
  readonly isValid: WritableSignal<boolean> = signal(false);
  readonly formErrorText: WritableSignal<string | null> = signal(null);

  readonly currentErrorText = computed(() => this.errorText() || this.formErrorText());
  readonly hasError = computed(() => this.stateElement() === 'error' || !!this.currentErrorText());

  model = model<string>('');
  isDirty: WritableSignal<boolean> = signal(false);
  isFocused: WritableSignal<boolean> = signal(false);
  groupText: WritableSignal<string> = signal('');
  groupIcon: WritableSignal<UiIconModel> = signal({});

  inputRef = viewChild<ElementRef<HTMLInputElement>>('input');
  @ViewChildren(Tooltip) tooltip;

  @Input() set inputFocus(value: boolean) {
    setTimeout(() => {
      const inputEl = this.inputRef()?.nativeElement;
      if (inputEl) {
        value ? inputEl.focus() : inputEl.blur();
      }
      this.isFocused.set(value);
    }, 0);
  }

  @Output() onChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() onFocus: EventEmitter<any> = new EventEmitter<any>();
  @Output() onBlur: EventEmitter<any> = new EventEmitter<any>();
  @Output() onEnter: EventEmitter<any> = new EventEmitter<any>();
  @Output() onClear: EventEmitter<any> = new EventEmitter<any>();
  @Output() onIconClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() onKeydown: EventEmitter<any> = new EventEmitter<any>();
  @Output() onRightIconClick: EventEmitter<any> = new EventEmitter<any>();

  get inputElement() {
    return this.inputRef()?.nativeElement || null;
  }

  readonly shouldShowClearIcon = computed(() => {
    const hasValue = (this.model() && this.model() !== '' && this.model() !== null) || this.formControl()?.value;
    const isNotDisabledOrReadonly = this.stateElement() !== 'disabled' && this.stateElement() !== 'readonly';

    if (!isNotDisabledOrReadonly) {
      return false;
    }

    switch (this.clearIconBehavior()) {
      case 'never':
        return false;
      case 'focus-and-dirty':
        return hasValue && this.isFocused() && this.isDirty();
      case 'focus':
        return hasValue && this.isFocused();
      case 'filled-only':
        return hasValue && this.isDirty();
      case 'search':
        return hasValue;
    }
  });

  readonly rightIconToShow = computed(() => {
    if (this.stateElement() === 'loading') {
      return 'loading';
    }
    if (this.shouldShowClearIcon()) {
      return 'clear';
    }
    if (this.hasCopyIcon()) {
      return 'copy';
    }
    if (this.icon() && this.iconPos() === 'right') {
      return 'regular';
    }
    return 'none';
  });

  readonly inputTooltipText = computed(() => {
    const value = this.model();
    return this.isOverflowText() && value ? value : '';
  });

  readonly copyText = computed(() => (this.onCopy() ? 'Copied!' : 'Copy'));

  onCopyIconMouseLeave() {
    if (this.onCopy()) {
      this.onCopy.set(false);
      if (this.tooltip?.last) {
        this.tooltip.last.hide();
        setTimeout(() => {
          this.tooltip.last.show();
        }, 0);
      }
    }
  }

  ngOnInit(): void {
    this.initView();
    this.initErrorFormControl();
  }

  ngAfterViewInit(): void {
    if (this.selectTextAfterInit()) {
      this.selectText();
    }
  }

  private initErrorFormControl() {
    const formControl = this.fieldControl();
    if (formControl) {
      const errors = formControl.errors;
      const shouldShowError = (formControl.touched || formControl.dirty) && errors;
      this.formErrorText.set(shouldShowError ? this.errorsTexts()?.[Object.keys(errors)[0]] || null : null);

      this.fieldControl()
        .statusChanges.pipe(untilDestroyed(this))
        .subscribe(() => {
          const errors = this.fieldControl().errors;
          this.formErrorText.set(errors ? this.errorsTexts()?.[Object.keys(errors)[0]] || null : null);
        });
    }
  }

  getBaseClasses() {
    const superClasses = super.getBaseClasses();
    return {
      ...superClasses,
      'u-input-regular': this.styleType() === 'regular',
      'u-input-inline': this.styleType() === 'inline',
      'u-input-group': this.styleType() === 'group',
      'u-input-lightweight': this.editingLightweightText() === true && this.isFocused() === false,
      'u-element-error': superClasses['u-element-error'] || this.formErrorText(),
    };
  }

  initView() {
    if (typeof this.groupContent() === 'string') {
      this.groupText.set(this.groupContent() as string);
    } else {
      this.groupIcon.set(this.groupContent() as UiIconModel);
    }
  }

  selectText() {
    setTimeout(() => {
      const el = this.inputRef()?.nativeElement;
      if (el) {
        el.select();
        el.focus();
      }
    }, 0);
  }

  focusInput() {
    this.inputElement?.focus({ preventScroll: true });
  }

  focusInputEnd() {
    const inputEl = this.inputRef()?.nativeElement;
    if (inputEl) {
      inputEl.focus();
      inputEl.setSelectionRange(inputEl.value.length, inputEl.value.length);
    }
  }

  blurInput() {
    this.inputElement?.blur();
  }

  onChangeEvent(value: string) {
    this.model.set(value);
    this.isDirty.set(true);
    this.onChange.emit(value);
  }

  onFocusEvent() {
    this.isFocused.set(true);
    if (this.editingHighlight() === true) {
      this.selectText();
    }
    this.onFocus.emit({ value: this.model() });
  }

  onBlurEvent() {
    this.isFocused.set(false);
    this.onBlur.emit({ value: this.model() });
  }

  onKeydownEvent($event: KeyboardEvent) {
    if (isEnterKey($event.key)) {
      this.onEnter.emit({ event: $event, value: this.model() });
    }
    this.onKeydown.emit($event);
  }

  onClearEvent($event: MouseEvent) {
    $event.stopImmediatePropagation();
    $event.stopPropagation();
    $event.preventDefault();
    this.model.set('');

    const control = this.fieldControl?.();
    if (control) {
      control.setValue('');
      control.markAsDirty();
      control.markAsTouched();
      control.updateValueAndValidity();
    }

    this.onChange.emit('');
    this.onClear.emit();

    if (this.inputRef() && this.inputRef()?.nativeElement) {
      this.inputRef()!.nativeElement.focus();
    }
  }

  onCopyEvent() {
    this.onCopy.set(true);
    navigator.clipboard.writeText(this.model());
    if (this.tooltip?.last) {
      this.tooltip.last.hide();
      setTimeout(() => {
        this.tooltip.last.show();
      }, 0);
    }
  }

  onIconClickEvent($event: MouseEvent) {
    this.onIconClick.emit({ event: $event, value: this.model() });
  }

  onRightIconClickEvent($event: MouseEvent) {
    this.onRightIconClick.emit({ event: $event, value: this.model() });
  }

  getIconRef() {
    return null;
  }

  updateTooltipOnHover(): void {
    const el = this.inputRef()?.nativeElement;
    if (el) {
      this.isOverflowText.set(el.offsetWidth < el.scrollWidth);
    }
  }
}
